import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { EventWithResources as Event } from '@/types/event';
import { EventInput } from '@fullcalendar/core';
import { createEventColor } from '@/enums/event-colors';
import { addWeeks, format, subWeeks } from 'date-fns';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function shortenName(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('');
}

export function mapRecursively<T extends { id?: string; children?: T[] }>(
  items: T[],
  callback: (item: T) => T,
) {
  return items.map((item) => {
    const newItem = callback(item);
    if (newItem.children && newItem.children.length > 0) {
      newItem.children = mapRecursively(newItem.children, callback);
    }
    return newItem;
  });
}

export function forEachRecursively<T extends { id?: string; children?: T[] }>(
  items: T[],
  callback: (item: T) => void,
) {
  items.forEach((item) => {
    callback(item);
    if (item.children && item.children.length > 0) {
      forEachRecursively(item.children, callback);
    }
  });
}

export function getRecursiveLevel<T extends { getParent: () => T | null }>(item: T | null) {
  let level = 0;
  let currentItem = item;

  while (currentItem && currentItem.getParent() !== null) {
    currentItem = currentItem.getParent();
    level++;
  }

  return level;
}

export function filterDuplicates(array: { id?: string }[]) {
  const seen = new Set<string>();

  // Filter out duplicates
  return array.filter((item) => {
    const id = item.id ?? '';
    if (seen.has(id)) {
      return false; // Duplicate found, exclude it
    }
    seen.add(id);
    return true; // Unique item, include it
  });
}

export function reorderArrayByIndex<T extends { index?: number }>(
  array: T[],
  dragIndex: number,
  hoverIndex: number,
): T[] {
  const updatedArray = [...array];
  const [draggedItem] = updatedArray.splice(dragIndex, 1); // Remove the dragged item
  updatedArray.splice(hoverIndex, 0, draggedItem); // Insert it at the hover index

  // Update the index property of all items
  return updatedArray.map((item, newIndex) => ({
    ...item,
    index: newIndex,
  }));
}

export function sortByIndex<T extends { index?: number }>(array: T[]) {
  return Array.from(array).sort((a, b) => {
    const indexA = a.index ?? 0;
    const indexB = b.index ?? 0;
    return indexA - indexB;
  });
}

export function sortByTitle<T extends { title?: string }>(array: T[]) {
  return Array.from(array).sort((a, b) => {
    const titleA = a.title?.toLowerCase() ?? '';
    const titleB = b.title?.toLowerCase() ?? '';
    return titleA.localeCompare(titleB);
  });
}

export function replaceObjectInArray<T extends { id: string }>(item: T, items: T[]) {
  return items.map((i) => {
    if (i.id === item.id) {
      return {
        ...item,
      };
    }
    return i;
  });
}

export function convertToCalendarEvent(
  e: Event,
  resourceIds?: string[],
  constraint?: {
    resourceIds: string[];
  },
): EventInput {
  const color = e.color ? createEventColor(e.color) : undefined;
  const isCustom = color?.isCustom === true && color.style !== undefined;
  return {
    id: e.id,
    title: e.title,
    start: e.start,
    end: e.end,
    editable: e.id !== undefined && e.id !== 'undefined',
    classNames: !color
      ? ['!bg-sky-200', '!border-sky-200']
      : isCustom
        ? []
        : [color.backgroundColor, color.borderColor],
    // Custom hex colors can't be expressed as Tailwind classes, so apply them as inline styles.
    backgroundColor: isCustom ? color!.style!.backgroundColor : undefined,
    borderColor: isCustom ? color!.style!.borderColor : undefined,
    textColor: isCustom ? color!.style!.color : undefined,
    resourceIds: resourceIds ? resourceIds : e.resources.map((r) => r.resourceId),
    constraint,
    extendedProps: {
      color: color ? color.color : 'sky',
      status: e.status,
      description: e.description,
      originalResourceIds: e.resources.map((r) => r.resourceId),
      documents: e.documents,
    },
  };
}

export function dateWithoutTz(date: Date) {
  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() + userTimezoneOffset);
}

export function extractInteger(str: string | undefined): number | undefined {
  if (!str) {
    return undefined;
  }
  const match = str.match(/-?\d+/);
  return match ? parseInt(match[0], 10) : undefined;
}

export function buildPagingUrl(
  postfix: 'events' | 'requirements' | 'time-offs',
  scheduleId: string,
  shareToken?: string | undefined,
  start?: Date | undefined,
  end?: Date | undefined,
  skipDateFilter?: boolean | undefined,
) {
  const mainUrl = shareToken
    ? `/api/share/${shareToken}/${postfix}`
    : `/api/schedules/${scheduleId}/${postfix}`;

  // If skipDateFilter is true, return URL without date parameters
  if (skipDateFilter) {
    return mainUrl;
  }

  // Original logic: require start and end if not skipping date filter
  if (!start || !end) {
    return null;
  }

  const params = new URLSearchParams();

  params.append('start', format(subWeeks(start, 1), 'yyyy-MM-dd'));

  params.append('end', format(addWeeks(end, 1), 'yyyy-MM-dd'));

  return `${mainUrl}?${params.toString()}`;
}

export function extractWeeks(str: string | undefined): number | undefined {
  if (!str) {
    return undefined;
  }
  const match = str.match(/(\d+)-weeks-*/);
  return match ? parseInt(match[0], 10) : undefined;
}

export function findMinDate<T extends { start: Date | string }>(items: T[]): Date | undefined {
  if (items.length === 0) {
    return undefined;
  }
  const sorted = items.sort((a, b) => {
    const dateA = new Date(a.start).getTime();
    const dateB = new Date(b.start).getTime();
    return dateA - dateB;
  });

  return new Date(sorted[0].start);
}

export function findMaxDate<T extends { end: Date | string }>(items: T[]): Date | undefined {
  if (items.length === 0) {
    return undefined;
  }
  const sorted = items.sort((a, b) => {
    const dateA = new Date(a.end).getTime();
    const dateB = new Date(b.end).getTime();
    return dateA - dateB;
  });

  return new Date(sorted[sorted.length - 1].end);
}
export function isWeekly (currentViewType: string) {
  return currentViewType === 'resourceTimeline32w';
};
